import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import "./Hisobkitob.css";
import api from "../api";
import { useSklad } from "../SkladContext";

/* ================= Helpers ================= */
const normDecStr = (v) => {
  if (v == null) return "";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (["", "-", ".", "-."].includes(s)) return "";

  return s;
};
const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};
const toFixed3Str = (v) => {
  const n = Math.round(toNum(v) * 1000);
  const neg = n < 0;
  const abs = Math.abs(n);
  const ip = String(Math.floor(abs / 1000));
  const fp = String(abs % 1000).padStart(3, "0");
  return (neg ? "-" : "") + ip + "." + fp;
};
const moneyUSD = (v, { min = 2, max = 2 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max }) + " so'm";
const fmtQty = (v) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: 0, maximumFractionDigits: 3 });

const toLocalYMD = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const todayStr = () => toLocalYMD(new Date());
const normalizeDate = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return toLocalYMD(d);
  const m = s.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : "";
};

const toIntScaled = (v, scale) => {
  const s = normDecStr(v);
  const neg = s.startsWith("-");
  const body = neg ? s.slice(1) : s;
  const [ipRaw, fpRaw = ""] = body.split(".");
  const ip = ipRaw || "0";
  const fp = (fpRaw + "0".repeat(scale)).slice(0, scale);
  const n = Number(ip + fp);
  return neg ? -n : n;
};
const mulDec = (a, b, scaleA = 4, scaleB = 3) => {
  const ai = toIntScaled(a, scaleA);
  const bi = toIntScaled(b, scaleB);
  const denom = 10 ** (scaleA + scaleB);
  return (ai * bi) / denom;
};

/* ================= Status ================= */
const UI_STATES = { OVERDUE: "overdue_debtor", NOT_DUE: "debtor_not_due", NEUTRAL: "neutral", CREDITOR: "creditor" };
const normalizeBool = (v) => v === true || v === 1 || v === "1" || (typeof v === "string" && v.toLowerCase() === "true");
const SERVER2UI_STATUS = (s) => {
  switch ((s || "").toLowerCase()) {
    case "overdue_debtor": return UI_STATES.OVERDUE;
    case "debtor_not_due": return UI_STATES.NOT_DUE;
    case "neutral":        return UI_STATES.NEUTRAL;
    case "creditor":       return UI_STATES.CREDITOR;
    default:               return null;
  }
};
const computeUiStatus = ({ signedDebt, hasOverdue, dueDate }) => {
  const bal = toNum(signedDebt || 0);
  const due = normalizeDate(dueDate);
  if (bal < 0) {
    if (normalizeBool(hasOverdue)) return UI_STATES.OVERDUE;
    if (due && due <= todayStr()) return UI_STATES.OVERDUE;
    return UI_STATES.NOT_DUE;
  }
  if (bal > 0) return UI_STATES.CREDITOR;
  return UI_STATES.NEUTRAL;
};
const stateColor = (ui) =>
  ui === UI_STATES.OVERDUE ? "red" :
  ui === UI_STATES.NOT_DUE ? "yellow" :
  ui === UI_STATES.CREDITOR ? "green" : "gray";
const stateLabel = (ui) =>
  ui === UI_STATES.OVERDUE || ui === UI_STATES.NOT_DUE ? "Qarzdor" :
  ui === UI_STATES.CREDITOR ? "Haqdor" : "Neytral";

const cx = (...a) => a.filter(Boolean).join(" ");
const asArr = (res) => Array.isArray(res?.data) ? res.data : res?.data?.results || [];
const hasNext = (res) => Boolean(res?.data?.next);
const sellerSourceFromFilter = (value) => {
  if (value === "bozor") return "bozor";
  if (value === "sotuv") return "dastur";
  return "";
};
const sourceLabel = (value) => (value === "bozor" ? "Bozor" : "Sotuv");

/* ================= UI satrlar ================= */
const SellerRow = React.memo(function SellerRow({ item, checked, onToggle }) {
  return (
    <label className="seller-row" style={{ contain: "content" }}>
      <input type="radio" checked={checked} onChange={() => onToggle(item.id)} />
      <div className="sr-body">
        <div className="m-left">
          <div className="m-buyer">{item.name}</div>
          <div className="m-brand">{item.brand}</div>
          <div className="m-meta">
            {item.phone && <div className="m-phone">{item.phone}</div>}
            <div className="m-source">{item.sourceLabel}</div>
          </div>
        </div>
        <div className={cx("m-badge", item.color)}>{item.label}</div>
        <div className={cx("m-amount", item.color)}>{moneyUSD(item.amountAbs)}</div>
      </div>
    </label>
  );
});

const SaleRow = React.memo(function SaleRow({ s }) {
  const rowTone = s.isCredit ? (s.isOverdue ? "red" : "yellow") : "plain";
  const badge = s.payType === "naxt" ? "green" : "orange";
  const lineTotal = mulDec(s.unitUsd, s.qty, 4, 3);
  return (
    <div className={cx("sale-row", rowTone)} style={{ contain: "content" }}>
      <div className="sr-title">
        <span className="pill pill-soft">{s.product}</span>
        <span className="muted">{s.sku}</span>
        <span className="muted">
          {s.date ? new Date(s.date).toLocaleDateString("uz-UZ") : "—"}
        </span>
        <span className="muted">
          Sotilgan narxi: {moneyUSD(s.unitUsd, { min: 2, max: 4 })}
        </span>
        <span className="muted">{fmtQty(s.qty)} dona</span>
        <span className="pill blue">{s.sourceLabel}</span>
        {s.sellerName && <span className="muted">Haridor: {s.sellerName}</span>}
        {s.soldBy && <span className="muted">Sotgan: {s.soldBy}</span>}
        {s.note && <span className="muted">Izoh: {s.note}</span>}
        <span className={cx("pill", badge)}>
          {s.payType === "naxt" ? "Naqtga" : "Nasiya"}
        </span>
      </div>
      <div className="sr-right">{moneyUSD(lineTotal)}</div>
    </div>
  );
});

/* ================= Payment Modal ================= */
function PaymentModal({
  open,
  onClose,
  sellerName,
  sellerSignedDebtAmount = 0,
  onSave,
}) {
  const [cashSom, setCashSom] = useState("");
  const [cardSom, setCardSom] = useState("");
  const [terminalSom, setTerminalSom] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setCashSom("");
      setCardSom("");
      setTerminalSom("");
      setNote("");
    }
  }, [open]);

  if (!open) return null;

  const totalPayment = toNum(cashSom) + toNum(cardSom) + toNum(terminalSom);
  const newSignedAfter = toNum(sellerSignedDebtAmount) + totalPayment;
  const canSave = totalPayment > 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e)=>e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">
            <b>Haridor:</b> {sellerName || "-"} | <b>Nasiya to'lovi</b>
          </div>
          <button className="btn" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          <label className="form-row">
            <span>Naqd so'm:</span>
            <input
              className="input"
              inputMode="numeric"
              value={cashSom}
              onChange={(e)=>setCashSom(e.target.value.replace(/[^\d]/g,""))}
            />
          </label>
          <label className="form-row">
            <span>Karta so'm:</span>
            <input
              className="input"
              inputMode="numeric"
              value={cardSom}
              onChange={(e)=>setCardSom(e.target.value.replace(/[^\d]/g,""))}
            />
          </label>
          <label className="form-row">
            <span>Terminal so'm:</span>
            <input
              className="input"
              inputMode="numeric"
              value={terminalSom}
              onChange={(e)=>setTerminalSom(e.target.value.replace(/[^\d]/g,""))}
            />
          </label>
          <label className="form-row">
            <span>Izoh:</span>
            <textarea
              className="input"
              rows={3}
              value={note}
              onChange={(e)=>setNote(e.target.value)}
            />
          </label>

          <div className="muted">
            To'lov jami: <b>{moneyUSD(totalPayment)}</b> - Yangi balans (signed): <b>{moneyUSD(newSignedAfter)}</b>
          </div>
        </div>
        <div className="modal-foot">
          <button
            className="btn btn-primary"
            disabled={!canSave}
            onClick={()=>{
              onSave?.({
                cash_som: Math.round(toNum(cashSom || 0)),
                card_som: Math.round(toNum(cardSom || 0)),
                terminal_som: Math.round(toNum(terminalSom || 0)),
                note: note.trim()
              });
              onClose();
            }}
          >
            Saqlash
          </button>
        </div>
      </div>
    </div>
  );
}
/* ================= Main Page ================= */
export default function Hisobkitob() {
  /* qidiruv */
  const [qSeller, setQSeller] = useState("");
  const [qProduct, setQProduct] = useState("");
  const [debQSeller, setDebQSeller] = useState("");
  const [debQProduct, setDebQProduct] = useState("");
  useEffect(()=>{
    const t=setTimeout(()=>setDebQSeller(qSeller.trim()),300);
    return ()=>clearTimeout(t);
  },[qSeller]);
  useEffect(()=>{
    const t=setTimeout(()=>setDebQProduct(qProduct.trim()),300);
    return ()=>clearTimeout(t);
  },[qProduct]);

  /* ko‘rinish + tanlov */
  const [view, setView] = useState("sellers");
  const [selectedSellerIds, setSelectedSellerIds] = useState([]);
  const oneSelected = selectedSellerIds.length === 1 ? selectedSellerIds[0] : null;

  /* filtrlar */
  const { activeSklad } = useSklad();
  const [payFilter, setPayFilter] = useState("all"); // all | naxt | nasiya
  const [sourceFilter, setSourceFilter] = useState("all"); // all | sotuv | bozor
  const [warehouse, setWarehouse] = useState(activeSklad);
  useEffect(() => { setWarehouse(activeSklad); }, [activeSklad]);

  // Sana filtri – doim bugungi kun bilan
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());

  // Kassir (account) bo‘yicha filtr
  const [userFilter, setUserFilter] = useState("all");
  const [userOptions, setUserOptions] = useState([]);

  /* modal */
  const [openModal, setOpenModal] = useState(false);

  /* data */
  const [sellers, setSellers] = useState([]);
  const [sales, setSales] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  /* paging */
  const SELLERS_PAGE_SIZE = 50, SALES_PAGE_SIZE = 50;
  const [sellersPage, setSellersPage] = useState(1);
  const [sellersHasMore, setSellersHasMore] = useState(true);
  const sellerLoaderRef = useRef(null);
  const [loadingSellers, setLoadingSellers] = useState(false);

  const [salesPage, setSalesPage] = useState(1);
  const [salesHasMore, setSalesHasMore] = useState(true);
  const salesLoaderRef = useRef(null);
  const [loadingSales, setLoadingSales] = useState(false);

  const sellersAbortRef = useRef(null);
  const salesAbortRef = useRef(null);

  /* omborlar */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("sklad/");
        const W = (Array.isArray(data) ? data : data?.results || [])
          .map(w => ({ id:String(w.id), name:w.nom || w.name || "" }));
        setWarehouses([{ id:"all", name:"Barchasi" }, ...W]);
      } catch (e) {
        console.log("WAREHOUSE ERR", e?.response?.status, e?.response?.data);
        alert("Sklad ro‘yxatini yuklashda xatolik.");
      }
    })();
  }, []);

  /* params builders */
  const buildSellerParams = useCallback((pageNum)=>{
    const p = new URLSearchParams();
    p.set("page",String(pageNum));
    p.set("page_size",String(SELLERS_PAGE_SIZE));
    const sellerSource = sellerSourceFromFilter(sourceFilter);
    if (sellerSource) p.set("source", sellerSource);
    // Sklad filtri: tanlangan skladga biriktirilgan + global haridorlar
    if (warehouse && warehouse !== "all") p.set("sklad", String(warehouse));
    return p;
  },[sourceFilter, warehouse]);

  const buildSalesParams = useCallback((pageNum)=>{
    const p = new URLSearchParams();
    p.set("page",String(pageNum));
    p.set("page_size",String(SALES_PAGE_SIZE));
    if (selectedSellerIds.length) p.set("seller_ids", selectedSellerIds.join(","));
    if (warehouse !== "all") p.set("warehouse", warehouse);
    if (payFilter !== "all") p.set("pay_type", payFilter === "naxt" ? "naqd" : "nasiya");
    if (sourceFilter !== "all") p.set("source", sourceFilter);
    if (debQProduct) p.set("search", debQProduct);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p;
  },[selectedSellerIds, warehouse, payFilter, sourceFilter, debQProduct, dateFrom, dateTo]);

  /* sellers fetch */
  const fetchSellers = useCallback(async (pageNum, replace=false)=>{
    try { sellersAbortRef.current?.abort(); } catch {}
    const controller = new AbortController();
    sellersAbortRef.current = controller;
    setLoadingSellers(true);
    try {
      const res = await api.get(`sotuvchi/?${buildSellerParams(pageNum).toString()}`, { signal: controller.signal });
      const arr = asArr(res).map(s=>{
        const signed = s.qarhaq ?? 0;
        const due = normalizeDate(s.next_due_date);
        const hasOver = normalizeBool(s.has_overdue);
        const srv = SERVER2UI_STATUS(s.current_status);
        const ui = srv || computeUiStatus({ signedDebt: signed, hasOverdue: hasOver, dueDate: due });
        return {
          id: String(s.id),
          name: s.ismi,
          brand: s.Dokonnomi || "",
          phone: s.telraqam1 || s.telraqam2 || "",
          source: s.source || "dastur",
          sourceLabel: sourceLabel(s.source),
          uiState: ui,
          color: stateColor(ui),
          label: stateLabel(ui),
          amountAbs: Math.abs(toNum(signed)),
          signedDebt: toNum(signed),
        };
      });
      setSellers(prev=> (replace ? arr : [...prev, ...arr]));
      setSellersHasMore(hasNext(res));
    } catch(e){
      if (e.name!=="CanceledError" && e.name!=="AbortError") {
        console.log("SELLERS ERR", e?.response?.status, e?.response?.data);
      }
      if (pageNum===1) setSellers([]);
      setSellersHasMore(false);
    } finally {
      setLoadingSellers(false);
    }
  },[buildSellerParams]);

  useEffect(()=>{
    setSellersPage(1);
    setSellers([]);
    setSellersHasMore(true);
    fetchSellers(1,true);
  },[buildSellerParams, fetchSellers]);

  useEffect(()=>{
    if (!sellerLoaderRef.current) return;
    const io = new IntersectionObserver(async([entry])=>{
      if (entry.isIntersecting && sellersHasMore && !loadingSellers && sellers.length>0){
        const next = sellersPage + 1;
        setSellersPage(next);
        await fetchSellers(next);
      }
    },{ rootMargin:"300px" });
    io.observe(sellerLoaderRef.current);
    return ()=>io.disconnect();
  },[sellerLoaderRef, sellersPage, sellersHasMore, loadingSellers, sellers.length, fetchSellers]);

  const toggleSeller = (id)=>{
    const sid = String(id);
    setSelectedSellerIds(prev => (prev.includes(sid) ? prev : [sid]));
  };

  useEffect(() => {
    setSelectedSellerIds((prev) => prev.filter((id) => sellers.some((seller) => seller.id === id)));
  }, [sellers]);

  const visibleSellers = useMemo(()=>{
    const q = debQSeller.toLowerCase();
    if (!q) return sellers;
    return sellers.filter((it) => {
      const name = (it.name || "").toLowerCase();
      const phone = (it.phone || "").toLowerCase();
      return name.includes(q) || phone.includes(q);
    });
  }, [sellers, debQSeller]);

  /* sales fetch */
  const fetchSales = useCallback(async (pageNum, replace=false)=>{
    try { salesAbortRef.current?.abort(); } catch {}
    const controller = new AbortController();
    salesAbortRef.current = controller;
    setLoadingSales(true);
    try {
      const res = await api.get(`sotilgan-maxsulot/?${buildSalesParams(pageNum).toString()}`, { signal: controller.signal });
      const today = todayStr();
      const mapped = asArr(res).map(r=>{
        const isCredit = r.tolov_turi === "nasiya";
        const dline = normalizeDate(r.nmuddat);
        const overdue = isCredit && dline && dline <= today;
        return {
          id: r.id,
          buyerId: String(r.sotuvchi),
          product: r.maxsulot_nom || r.maxsulot?.nom || "—",
          sku: r.maxsulot_seriyasi || r.maxsulot?.seriyasi || "—",
          date: r.created_at,
          unitUsd: String(r.narx ?? ""),
          qty: String(r.sotilgansoni ?? ""),
          payType: r.tolov_turi === "naqd" ? "naxt" : "nasiya",
          warehouse: String(r.sklad),
          source: r.source || "sotuv",
          sourceLabel: sourceLabel(r.source),
          isCredit,
          isOverdue: overdue,
          sellerName: r.sotuvchi_ismi || r.sotuvchi_nomi || "",
          soldBy: r.created_by_username || r.created_by || "",
          note: r.izoh || r.note || r.comment || "",
        };
      });
      setSales(prev => (replace ? mapped : [...prev, ...mapped]));
      setSalesHasMore(hasNext(res));
    } catch(e){
      if (e.name!=="CanceledError" && e.name!=="AbortError") {
        console.log("SALES ERR", e?.response?.status, e?.response?.data);
      }
      if (pageNum===1) setSales([]);
      setSalesHasMore(false);
    } finally {
      setLoadingSales(false);
    }
  },[buildSalesParams]);

  useEffect(()=>{
    setSalesPage(1);
    setSales([]);
    setSalesHasMore(true);
    fetchSales(1,true);
  },[buildSalesParams, fetchSales, view]);

  useEffect(()=>{
    if (!salesLoaderRef.current) return;
    const io = new IntersectionObserver(async([entry])=>{
      if (entry.isIntersecting && salesHasMore && !loadingSales && sales.length>0){
        const next = salesPage + 1;
        setSalesPage(next);
        await fetchSales(next);
      }
    },{ rootMargin:"300px" });
    io.observe(salesLoaderRef.current);
    return ()=>io.disconnect();
  },[salesLoaderRef, salesPage, salesHasMore, loadingSales, sales.length, fetchSales]);

  const dateInRange = useCallback((iso, from, to)=>{
    const d = normalizeDate(iso);
    if (!d) return true;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  },[]);

  // sotgan kassirlar ro'yxati (frontenddan)
  useEffect(() => {
    const seen = new Set();
    const opts = [];
    sales.forEach((s) => {
      if (s.soldBy) {
        const key = String(s.soldBy);
        if (!seen.has(key)) {
          seen.add(key);
          opts.push(key);
        }
      }
    });
    opts.sort((a, b) => a.localeCompare(b, "uz"));
    setUserOptions(opts);
  }, [sales]);

  const filteredSales = useMemo(()=>{
    return sales.filter(s=>{
      if (view==="sellers" && selectedSellerIds.length && !selectedSellerIds.includes(String(s.buyerId))) return false;
      if (!dateInRange(s.date, dateFrom, dateTo)) return false;
      if (payFilter!=="all" && s.payType !== payFilter) return false;
      if (sourceFilter!=="all" && s.source !== sourceFilter) return false;
      if (userFilter!=="all" && s.soldBy !== userFilter) return false;
      return true;
    });
  },[sales, view, selectedSellerIds, dateFrom, dateTo, dateInRange, payFilter, sourceFilter, userFilter]);

  const sumBy = (lst, pred=()=>true) =>
    lst.reduce((t,s)=> (pred(s) ? t + mulDec(s.unitUsd, s.qty, 4, 3) : t), 0);
  const totalCash = sumBy(filteredSales, s=> s.payType==="naxt");
  const totalCredit = sumBy(filteredSales, s=> s.payType==="nasiya");
  const totalOverdue = sumBy(filteredSales, s=> s.isCredit && s.isOverdue);
  const totalNotOverdue = sumBy(filteredSales, s=> s.isCredit && !s.isOverdue);
  const grandTotal = sumBy(filteredSales);

  const oneSellerObj = sellers.find(x=> x.id=== (oneSelected||""));
  const paymentBlocked = oneSellerObj?.source === "bozor";

  /* ====== To'lov (kassa + balans) ====== */
  const submitPayment = useCallback(async ({
    cash_som,
    card_som,
    terminal_som,
    note,
  })=>{
    if (!oneSelected) { alert("Avval 1 ta haridor tanlang."); return; }
    if (oneSellerObj?.source === "bozor") {
      alert("Bozor mijoziga kirim qilish mumkin emas.");
      return;
    }
    const sid = Number(oneSelected);
    const buyerName = oneSellerObj?.name || "";

    const cash = Math.round(toNum(cash_som));
    const card = Math.round(toNum(card_som));
    const terminal = Math.round(toNum(terminal_som));
    const totalAmount = cash + card + terminal;

    try {
      // 1) Kassaga kirim
      await api.post("kassa/income/", {
        buyer_name: buyerName,
        cash_som: cash,
        card_som: card,
        terminal_som: terminal,
        note: (note || "").trim(),
      });

      // 2) Payment yozuvi (balans avtomatik yangilanadi)
      await api.post("payment/", {
        sotuvchi: sid,
        amount: toFixed3Str(totalAmount),
        method: "hisobkitob",
        note: (note || "").trim(),
      });

      // ro'yxatlarni yangilash
      setSellersPage(1); setSellers([]); setSellersHasMore(true);
      await fetchSellers(1,true);
      setSalesPage(1); setSales([]); setSalesHasMore(true);
      await fetchSales(1,true);

      alert("✅ To‘lov qabul qilindi va kassaga yozildi.");
    } catch (e) {
      console.log("PAYMENT ERR", e?.response?.status, e?.response?.data || e.message);
      const server = e?.response?.data;
      const msg = typeof server === "object" ? JSON.stringify(server) : (server || e.message);
      alert(`Xatolik: ${msg}`);
    }
  },[oneSelected, oneSellerObj, fetchSales, fetchSellers]);

  /* ================= Render ================= */
  return (
    <div className="hisobkitob-page">
      {/* TOP: qidiruvlar */}
      <div className="topbar fr-panel">
        <input
          className="input"
          placeholder="Haridor qidiring…"
          value={qSeller}
          onChange={(e)=>setQSeller(e.target.value)}
        />
        <input
          className="input"
          placeholder="Mahsulot qidiring…"
          value={qProduct}
          onChange={(e)=>setQProduct(e.target.value)}
        />
      </div>

      {/* FILTER BAR */}
      <div className="filterbar fr-panel">
        <div className="left-tabs">
          <button
            className={cx("tab", view==="sellers" && "active")}
            onClick={()=>setView("sellers")}
          >
            Haridorlar
          </button>
          <button
            className={cx("tab", view==="all" && "active")}
            onClick={()=>setView("all")}
          >
            Barchasi
          </button>
        </div>

        <div className="right-controls">
          <div className="seg">
            <button
              className={cx("seg-btn", payFilter==="all" && "on")}
              onClick={()=>setPayFilter("all")}
            >
              Naqt/Nasiya
            </button>
            <button
              className={cx("seg-btn", payFilter==="naxt" && "on")}
              onClick={()=>setPayFilter("naxt")}
            >
              Naqt
            </button>
            <button
              className={cx("seg-btn", payFilter==="nasiya" && "on")}
              onClick={()=>setPayFilter("nasiya")}
            >
              Nasiya
            </button>
          </div>

          <select
            value={sourceFilter}
            onChange={(e)=>setSourceFilter(e.target.value)}
          >
            <option value="all">Manba: barchasi</option>
            <option value="sotuv">Sotuv sahifasi</option>
            <option value="bozor">Bozor</option>
          </select>

          <div className="date-range">
            <input
              className="input"
              type="date"
              value={dateFrom}
              onChange={(e)=>setDateFrom(e.target.value)}
            />
            <span className="sep">—</span>
            <input
              className="input"
              type="date"
              value={dateTo}
              onChange={(e)=>setDateTo(e.target.value)}
            />
            <button
              className="btn btn-ghost"
              onClick={()=>{
                const t = todayStr();
                setDateFrom(t);
                setDateTo(t);
              }}
            >
              Tozalash
            </button>
          </div>

          {/* Kassir bo‘yicha filtr */}
          <select
            value={userFilter}
            onChange={(e)=>setUserFilter(e.target.value)}
          >
            <option value="all">Barcha kassirlar</option>
            {userOptions.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>

          <select
            value={warehouse}
            onChange={(e)=>setWarehouse(e.target.value)}
          >
            {warehouses.map(w=> (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>

          <button
            className="btn"
            disabled={paymentBlocked}
            onClick={()=>{
              if (selectedSellerIds.length !== 1) {
                alert("To‘lov uchun 1 ta haridor tanlang.");
                return;
              }
              setOpenModal(true);
            }}
          >
            {paymentBlocked ? "Bozor uchun yopiq" : "Kirim qilish"}
          </button>
        </div>
      </div>

      {/* GRID */}
      <div className="grid">
        {/* LEFT: sellers */}
        <div className="left fr-card">
          <div className="left-list">
            {visibleSellers.length ? (
              <>
                {visibleSellers.map(it=>(
                  <SellerRow
                    key={it.id}
                    item={it}
                    checked={selectedSellerIds.includes(String(it.id))}
                    onToggle={toggleSeller}
                  />
                ))}
                {loadingSellers && <div className="empty small">Yuklanmoqda…</div>}
                <div ref={sellerLoaderRef} style={{height:1}}/>
              </>
            ) : loadingSellers ? (
              <div className="empty">Yuklanmoqda…</div>
            ) : (
              <div className="empty">Hech narsa topilmadi</div>
            )}
          </div>
        </div>

        {/* RIGHT: sales */}
        <div className="right fr-card">
          {filteredSales.map(s => <SaleRow key={s.id} s={s} />)}
          {!filteredSales.length && !loadingSales && (
            <div className="empty">Maʼlumot topilmadi</div>
          )}
          {loadingSales && <div className="empty small">Yuklanmoqda…</div>}
          <div ref={salesLoaderRef} style={{height:1}}/>
        </div>
      </div>

      {/* BOTTOM SUM */}
      <div className="bottom-sum fr-panel">
        <div className="chip chip-yellow">
          Muddati o‘tmagan nasiyalar: <b>{moneyUSD(totalNotOverdue)}</b>
        </div>
        <div className="chip chip-yellow">
          Umumiy nasiyalar: <b>{moneyUSD(totalCredit)}</b>
        </div>
        <div className="chip chip-red">
          Muddati o‘tgan nasiyalar: <b>{moneyUSD(totalOverdue)}</b>
        </div>
        <div className="chip chip-green">
          Naqt savdo: <b>{moneyUSD(totalCash)}</b>
        </div>
        <div className="chip chip-green strong">
          Jami savdo: <b>{moneyUSD(grandTotal)}</b>
        </div>
      </div>

      {/* MODAL */}
      <PaymentModal
        open={openModal}
        onClose={()=>setOpenModal(false)}
        sellerName={oneSellerObj?.name}
        sellerSignedDebtAmount={oneSellerObj?.signedDebt || 0}
        onSave={submitPayment}
      />
    </div>
  );
}

